import axios from 'axios';

export default {
    namespaced: true,

    state: {
        authenticated: false,
        authenticationFailed: false,
        info: {}, // NEVER null
    },

    mutations: {
        SET_USER_INFO(state, payload) {
            state.info = payload || {}; // hard safety
        },

        LOAD_AUTHENTICATED_USER(state, payload) {
            state.authenticated = Boolean(payload);
        },

        LOGOUT_USER(state) {
            state.info = {};
            state.authenticated = false;
            document.cookie = 'token=; Max-Age=0; path=/';
        },

        CLEAN_USER_STATE(state) {
            state.authenticationFailed = false;
            state.authenticated = false;
            state.info = {};
        },
    },

    actions: {
        /* -------------------------
         * AUTH BOOTSTRAP (on refresh)
         * ------------------------- */
        async initAuth({ commit }) {
            const token = getCookie('token');

            if (!token) {
                commit('LOAD_AUTHENTICATED_USER', false);
                commit('SET_USER_INFO', {});
                return;
            }

            axios.defaults.headers.common.Authorization = `Bearer ${token}`;

            try {
                const { data } = await axios.get('/api/user');
                commit('SET_USER_INFO', data);
                commit('LOAD_AUTHENTICATED_USER', true);
            } catch (e) {
                console.warn('initAuth failed', e?.response?.status);
                commit('LOAD_AUTHENTICATED_USER', false);
                commit('SET_USER_INFO', {});
                document.cookie = 'token=; Max-Age=0; path=/';
            }
        },

        /* -------------------------
         * LOGIN
         * ------------------------- */
        async authenticateUser({ commit }, payload) {
            commit('CLEAN_USER_STATE');

            try {
                const { data } = await axios.post('/api/auth', payload);
                const token = data?.authToken;

                if (!token) {
                    throw new Error('No token returned from /api/auth');
                }

                axios.defaults.headers.common.Authorization = `Bearer ${token}`;

                const expires = new Date(Date.now() + 3600 * 1000).toUTCString();
                document.cookie = `token=${token}; expires=${expires}; path=/`;

                // Fetch authenticated user immediately
                const me = await axios.get('/api/user');
                commit('SET_USER_INFO', me.data);
                commit('LOAD_AUTHENTICATED_USER', true);

                return true;
            } catch (error) {
                commit('SET_USER_INFO', {});
                commit('LOAD_AUTHENTICATED_USER', false);
                throw error;
            }
        },

        async authenticateGoogleUser(_, payload) {
            return axios.post('/api/auth/google', payload);
        },

        /* -------------------------
         * USER DATA
         * ------------------------- */
        async loadAuthenticatedUser({ state, commit }) {
            if (state.authenticated && Object.keys(state.info).length) {
                return state.info;
            }

            try {
                const { data } = await axios.get('/api/user');
                commit('SET_USER_INFO', data);
                commit('LOAD_AUTHENTICATED_USER', true);
                return data;
            } catch {
                commit('LOAD_AUTHENTICATED_USER', false);
                commit('SET_USER_INFO', {});
            }
        },

        async refreshAuthenticatedUser({ commit }) {
            try {
                const { data } = await axios.get('/api/user');
                commit('SET_USER_INFO', data);
                commit('LOAD_AUTHENTICATED_USER', true);
                return data;
            } catch {
                commit('LOAD_AUTHENTICATED_USER', false);
                commit('SET_USER_INFO', {});
            }
        },

        async logoutUser({ commit }) {
            commit('LOGOUT_USER');
        },

        /* -------------------------
         * API HELPERS (unchanged)
         * ------------------------- */
        async registerUser(_, payload) {
            return axios.post('/api/user', payload);
        },

        async getStatistics() {
            const { data } = await axios.get('/api/stats/user');
            return data;
        },

        async updateUser(_, payload) {
            return axios.post('/api/user/me', payload);
        },

        async updateVendor(_, payload) {
            return axios.post('/api/vendor/' + payload.id, payload);
        },

        async confirmEmail(_, payload) {
            return axios.post('/api/user/confirmation', payload);
        },

        async requestForgottenPasswordMail(_, payload) {
            return axios.post('/api/user/forgotten-password/request', payload);
        },

        async requestChangePasswordMail(_, payload) {
            return axios.post('/api/user/change-password/request', payload);
        },

        async changePassword(_, payload) {
            return axios.post('/api/user/forgotten-password/confirmation', payload);
        },

        async getUser(_, id) {
            const { data } = await axios.get('/api/user/' + id);
            return data;
        },
    },
};

function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
}